import { clsx } from 'clsx';
import {
  Brain,
  BarChart3,
  Target,
  AlertTriangle,
  Sparkles,
  MessageCircle,
  ThumbsUp,
  ThumbsDown,
  Minus,
  TrendingUp,
  TrendingDown,
  Zap,
  RefreshCw,
  Mic,
  ChevronRight,
} from 'lucide-react';
import { Card, CardHeader } from '../common/Card';
import { InfoTooltip, TOOLTIPS } from '../common/InfoTooltip';
import { ScoreGauge, LinearGauge } from '../dashboard/ScoreGauge';
import { StockPriceChart } from '../charts/ComparisonChart';
import { formatCurrency, formatPercent, formatSignal, capitalizeWord } from '../../utils/format';
import type { EnhancedAnalysis, NotablePost, EarningsTranscriptsResponse, EarningsTranscriptSummary } from '../../types';
import type { StockPriceData } from '../../utils/api';
import { VerdictBadge, SentimentIndicator, ToneBadge } from './EarningsTab';

interface OverviewTabProps {
  analysis: EnhancedAnalysis;
  additionalPriceData: StockPriceData | null;
  transcriptsData: EarningsTranscriptsResponse | null;
  loadingTranscripts: boolean;
  onViewEarnings: () => void;
}

export function OverviewTab({ analysis, additionalPriceData, transcriptsData, loadingTranscripts, onViewEarnings }: OverviewTabProps) {
  // Earnings highlights data extraction
  const withSummary = transcriptsData?.transcripts?.filter(
    t => t.has_transcript && t.ai_summary
  ) || [];
  const recentQuarters = withSummary.slice(0, 4);
  const latestTranscript = recentQuarters[0];
  const latestSummary = latestTranscript?.ai_summary as EarningsTranscriptSummary | null;

  // Build cross-quarter topic frequency map (topics appearing in 2+ quarters)
  const recurringTopics: { topic: string; count: number; sentiment: string }[] = [];
  if (recentQuarters.length >= 2) {
    const topicCounts = new Map<string, { count: number; sentiment: string }>();
    for (const t of recentQuarters) {
      const summary = t.ai_summary as EarningsTranscriptSummary | null;
      if (!summary?.key_topics) continue;
      // Use a set to count each topic only once per quarter
      const seen = new Set<string>();
      for (const kt of summary.key_topics) {
        const key = kt.topic.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        const existing = topicCounts.get(key);
        if (existing) {
          existing.count++;
        } else {
          topicCounts.set(key, { count: 1, sentiment: kt.sentiment });
        }
      }
    }
    for (const [topic, { count, sentiment }] of topicCounts) {
      if (count >= 2) {
        recurringTopics.push({ topic, count, sentiment });
      }
    }
    recurringTopics.sort((a, b) => b.count - a.count);
  }

  const hasEarningsData = latestSummary != null;

  return (
    <>
      {/* Score Gauges */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="flex flex-col items-center py-6 relative">
          <div className="absolute top-3 right-3">
            <InfoTooltip content={TOOLTIPS.sentimentScore} position="left" />
          </div>
          <ScoreGauge
            score={analysis.sentiment?.sentiment_score || 0}
            label="Sentiment Score"
            size="lg"
          />
          <p className="text-sm text-[var(--color-text-muted)] mt-2">
            {analysis.sentiment?.sentiment_label || 'Unknown'}
          </p>
        </Card>

        <Card className="flex flex-col items-center py-6 relative">
          <div className="absolute top-3 right-3">
            <InfoTooltip content={TOOLTIPS.fundamentalScore} position="left" />
          </div>
          <ScoreGauge
            score={analysis.fundamental?.fundamental_score || 0}
            label="Fundamental Score"
            size="lg"
          />
          <p className="text-sm text-[var(--color-text-muted)] mt-2">
            Based on financial metrics
          </p>
        </Card>

        <Card className="flex flex-col items-center py-6 relative">
          <div className="absolute top-3 right-3">
            <InfoTooltip content={TOOLTIPS.disconnectScore} position="left" />
          </div>
          <ScoreGauge
            score={analysis.disconnect?.disconnect_score || 0}
            label="Disconnect Score"
            size="lg"
          />
          <p className="text-sm text-[var(--color-text-muted)] mt-2">
            {formatSignal(analysis.disconnect?.signal || 'HOLD')}
          </p>
        </Card>
      </div>

      {/* Stock Price Chart */}
      <Card className="p-4">
        {(additionalPriceData?.daily_prices?.length || analysis.price_history?.length) ? (
          <StockPriceChart
            data={additionalPriceData?.daily_prices || analysis.price_history || []}
            dailyData={additionalPriceData?.daily_prices || analysis.price_history || []}
            ticker={analysis.ticker}
          />
        ) : (
          <div className="h-80 flex flex-col items-center justify-center text-[var(--color-text-muted)]">
            <RefreshCw className="h-8 w-8 animate-spin mb-4" />
            <p>Loading price data...</p>
          </div>
        )}
      </Card>

      {/* Earnings Highlights */}
      {loadingTranscripts && (
        <Card>
          <div className="animate-pulse space-y-4 p-6">
            <div className="flex items-center gap-3">
              <div className="h-5 w-5 rounded bg-[var(--color-bg-tertiary)]" />
              <div className="h-5 w-40 rounded bg-[var(--color-bg-tertiary)]" />
            </div>
            <div className="flex gap-2">
              <div className="h-7 w-20 rounded-full bg-[var(--color-bg-tertiary)]" />
              <div className="h-7 w-24 rounded-full bg-[var(--color-bg-tertiary)]" />
              <div className="h-7 w-20 rounded-full bg-[var(--color-bg-tertiary)]" />
            </div>
            <div className="space-y-3">
              <div className="h-4 w-full rounded bg-[var(--color-bg-tertiary)]" />
              <div className="h-4 w-3/4 rounded bg-[var(--color-bg-tertiary)]" />
              <div className="h-4 w-5/6 rounded bg-[var(--color-bg-tertiary)]" />
            </div>
          </div>
        </Card>
      )}

      {!loadingTranscripts && hasEarningsData && latestTranscript && latestSummary && (
        <Card>
          <div className="p-6 space-y-5">
            {/* Header row */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Mic className="h-5 w-5 text-[var(--color-accent)]" />
                <div>
                  <h3 className="text-base font-semibold text-[var(--color-text-primary)]">
                    Earnings Highlights
                  </h3>
                  <p className="text-xs text-[var(--color-text-muted)]">
                    Latest: Q{latestTranscript.quarter} {latestTranscript.year}
                  </p>
                </div>
              </div>
              <button
                onClick={onViewEarnings}
                className="flex items-center gap-1 text-xs text-[var(--color-accent)] hover:text-[var(--color-accent-hover)] transition-colors"
              >
                View full timeline
                <ChevronRight className="h-3.5 w-3.5" />
              </button>
            </div>

            {/* Badge row */}
            <div className="flex flex-wrap gap-2">
              {latestSummary.beat_or_miss && (
                <VerdictBadge verdict={latestSummary.beat_or_miss.verdict} label="Earnings" />
              )}
              {latestSummary.management_sentiment && (
                <ToneBadge tone={latestSummary.management_sentiment.overall_tone} />
              )}
              {latestSummary.forward_guidance && (
                <SentimentIndicator sentiment={latestSummary.forward_guidance.sentiment} />
              )}
            </div>

            {/* Key topics (up to 4) */}
            {latestSummary.key_topics && latestSummary.key_topics.length > 0 && (
              <div className="space-y-2.5">
                {latestSummary.key_topics.slice(0, 4).map((kt, i) => (
                  <div key={i} className="flex items-start gap-2.5">
                    <span
                      className={clsx(
                        'mt-1.5 h-2 w-2 rounded-full flex-shrink-0',
                        kt.sentiment === 'positive' && 'bg-emerald-400',
                        kt.sentiment === 'negative' && 'bg-red-400',
                        kt.sentiment === 'neutral' && 'bg-gray-400'
                      )}
                    />
                    <p className="text-sm text-[var(--color-text-secondary)]">
                      <span className="font-medium text-[var(--color-text-primary)]">{kt.topic}</span>
                      {' '}&mdash; {kt.detail}
                    </p>
                  </div>
                ))}
              </div>
            )}

            {/* Forward guidance */}
            {latestSummary.forward_guidance?.summary && (
              <div className="p-3.5 rounded-lg bg-blue-500/5 border-l-4 border-blue-500/50">
                <p className="text-sm text-[var(--color-text-secondary)]">
                  <span className="font-medium text-blue-400">Forward Guidance: </span>
                  {latestSummary.forward_guidance.summary}
                </p>
              </div>
            )}

            {/* Recurring themes */}
            {recurringTopics.length > 0 && (
              <div>
                <p className="text-xs font-medium text-[var(--color-text-muted)] mb-2">
                  Recurring Themes
                </p>
                <div className="flex flex-wrap gap-2">
                  {recurringTopics.slice(0, 6).map((rt, i) => (
                    <span
                      key={i}
                      className={clsx(
                        'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium',
                        rt.sentiment === 'positive' && 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20',
                        rt.sentiment === 'negative' && 'bg-red-500/10 text-red-400 border border-red-500/20',
                        rt.sentiment === 'neutral' && 'bg-gray-500/10 text-gray-400 border border-gray-500/20'
                      )}
                    >
                      {rt.topic.charAt(0).toUpperCase() + rt.topic.slice(1)}
                      <span className="opacity-60">{rt.count}x</span>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Compact timeline (last 3-4 quarters) */}
            {recentQuarters.length >= 2 && (
              <div className="border-t border-[var(--color-border)] pt-4">
                <p className="text-xs font-medium text-[var(--color-text-muted)] mb-3">
                  Recent Quarters
                </p>
                <div className="space-y-2">
                  {recentQuarters.slice(0, 4).map((t, i) => {
                    const s = t.ai_summary as EarningsTranscriptSummary | null;
                    if (!s) return null;
                    const verdict = s.beat_or_miss?.verdict;
                    const tone = s.management_sentiment?.overall_tone;
                    // Build a short summary from the first key topic
                    const shortSummary = s.key_topics?.[0]?.detail;
                    return (
                      <div
                        key={i}
                        className="flex items-center gap-3 text-xs py-1.5"
                      >
                        <span className="font-medium text-[var(--color-text-primary)] w-16 flex-shrink-0">
                          Q{t.quarter} {t.year}
                        </span>
                        {verdict && (
                          <span
                            className={clsx(
                              'px-1.5 py-0.5 rounded text-[10px] font-semibold w-10 text-center',
                              verdict === 'beat' && 'bg-emerald-500/15 text-emerald-400',
                              verdict === 'miss' && 'bg-red-500/15 text-red-400',
                              verdict === 'inline' && 'bg-gray-500/15 text-gray-400'
                            )}
                          >
                            {verdict === 'beat' ? 'Beat' : verdict === 'miss' ? 'Miss' : 'Inline'}
                          </span>
                        )}
                        {tone && (
                          <span
                            className={clsx(
                              'px-1.5 py-0.5 rounded text-[10px] font-medium',
                              tone === 'confident' && 'bg-emerald-500/10 text-emerald-400',
                              tone === 'optimistic' && 'bg-blue-500/10 text-blue-400',
                              tone === 'cautious' && 'bg-amber-500/10 text-amber-400',
                              tone === 'defensive' && 'bg-red-500/10 text-red-400',
                              tone === 'neutral' && 'bg-gray-500/10 text-gray-400'
                            )}
                          >
                            {tone.charAt(0).toUpperCase() + tone.slice(1)}
                          </span>
                        )}
                        {shortSummary && (
                          <span className="text-[var(--color-text-muted)] truncate">
                            {shortSummary}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* 200 DMA Status */}
      {analysis.technical?.ma_200d && analysis.technical.current_price > 0 && (() => {
        const price = analysis.technical.current_price;
        const ma200 = analysis.technical.ma_200d;
        const dist = ((price - ma200) / ma200) * 100;
        const classification =
          dist <= -20 ? 'Deeply Oversold' :
          dist <= -10 ? 'Oversold' :
          dist >= -5 && dist <= 5 ? 'Near MA' :
          dist <= 20 ? 'Above MA' :
          dist <= 30 ? 'Extended' : 'Very Extended';
        const badgeColor =
          dist <= -10 ? 'bg-emerald-500/15 text-emerald-400' :
          dist >= -5 && dist <= 5 ? 'bg-gray-500/10 text-gray-400' :
          dist >= 20 ? 'bg-red-500/15 text-red-400' :
          dist > 5 ? 'bg-yellow-500/10 text-yellow-500' :
          'bg-blue-500/10 text-blue-400';
        // Position indicator: map dist from -50% to +50% onto 0-100% bar
        const position = Math.min(100, Math.max(0, ((dist + 50) / 100) * 100));

        return (
          <Card>
            <CardHeader
              title="200-Day Moving Average"
              subtitle="Long-term trend indicator"
              icon={<BarChart3 className="h-5 w-5" />}
            />
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[var(--color-text-muted)]">Current Price</p>
                  <p className="text-xl font-bold text-[var(--color-text-primary)] font-mono">
                    {formatCurrency(price)}
                  </p>
                </div>
                <div className="text-center">
                  <span className={clsx('px-3 py-1 rounded-full text-sm font-medium', badgeColor)}>
                    {classification}
                  </span>
                  <p className={clsx(
                    'text-lg font-bold font-mono mt-1',
                    dist > 0 ? (dist > 20 ? 'text-red-500' : 'text-[var(--color-text-primary)]') : 'text-emerald-500'
                  )}>
                    {dist > 0 ? '+' : ''}{dist.toFixed(1)}%
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-[var(--color-text-muted)]">200 DMA</p>
                  <p className="text-xl font-bold text-[var(--color-text-secondary)] font-mono">
                    {formatCurrency(ma200)}
                  </p>
                  {dist < 0 && (
                    <p className="text-xs text-amber-500 font-mono mt-0.5">
                      +{(((ma200 - price) / price) * 100).toFixed(1)}% to reach
                    </p>
                  )}
                  {dist > 0 && (
                    <p className="text-xs text-red-400 font-mono mt-0.5">
                      {(((ma200 - price) / price) * 100).toFixed(1)}% to reach
                    </p>
                  )}
                </div>
              </div>

              {/* Visual position bar */}
              <div className="relative">
                <div className="h-3 bg-gradient-to-r from-emerald-500 via-gray-400 to-red-500 rounded-full opacity-30" />
                <div
                  className="absolute top-0 w-4 h-3 bg-[var(--color-text-primary)] rounded-full border-2 border-[var(--color-bg-primary)]"
                  style={{ left: `calc(${position}% - 8px)` }}
                />
                <div className="flex justify-between text-[10px] text-[var(--color-text-muted)] mt-1">
                  <span>Oversold</span>
                  <span>200 DMA</span>
                  <span>Extended</span>
                </div>
              </div>
            </div>
          </Card>
        );
      })()}

      {/* Analysis Details Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Sentiment Analysis */}
        <Card>
          <CardHeader
            title="Sentiment Analysis"
            subtitle="Social media and news sentiment"
            icon={<Brain className="h-5 w-5" />}
          />

          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-2 sm:gap-4">
              <div className="text-center p-2 sm:p-3 rounded-lg bg-emerald-500/10 relative">
                <div className="absolute top-1 right-1">
                  <InfoTooltip content={TOOLTIPS.bullishPercentage} position="bottom" />
                </div>
                <p className="text-lg sm:text-2xl font-bold text-emerald-500">
                  {analysis.sentiment?.bullish_percentage?.toFixed(0) || 0}%
                </p>
                <p className="text-[10px] sm:text-xs text-[var(--color-text-muted)]">Bullish</p>
              </div>
              <div className="text-center p-2 sm:p-3 rounded-lg bg-gray-500/10 relative">
                <div className="absolute top-1 right-1">
                  <InfoTooltip content={TOOLTIPS.neutralPercentage} position="bottom" />
                </div>
                <p className="text-lg sm:text-2xl font-bold text-gray-500">
                  {analysis.sentiment?.neutral_percentage?.toFixed(0) || 0}%
                </p>
                <p className="text-[10px] sm:text-xs text-[var(--color-text-muted)]">Neutral</p>
              </div>
              <div className="text-center p-2 sm:p-3 rounded-lg bg-red-500/10 relative">
                <div className="absolute top-1 right-1">
                  <InfoTooltip content={TOOLTIPS.bearishPercentage} position="bottom" />
                </div>
                <p className="text-lg sm:text-2xl font-bold text-red-500">
                  {analysis.sentiment?.bearish_percentage?.toFixed(0) || 0}%
                </p>
                <p className="text-[10px] sm:text-xs text-[var(--color-text-muted)]">Bearish</p>
              </div>
            </div>

            <div className="flex items-center gap-2 text-sm text-[var(--color-text-muted)]">
              <InfoTooltip content={TOOLTIPS.postsAnalyzed} position="right" />
              <span>
                Based on {analysis.sentiment?.posts_analyzed || 0} social posts analyzed
              </span>
            </div>

            {analysis.sentiment?.summary && (
              <div className="p-4 rounded-lg bg-[var(--color-bg-secondary)]">
                <p className="text-sm text-[var(--color-text-secondary)]">
                  {analysis.sentiment.summary}
                </p>
              </div>
            )}

            {analysis.sentiment?.key_themes && analysis.sentiment.key_themes.length > 0 && (
              <div>
                <div className="flex items-center gap-1 mb-2">
                  <p className="text-sm font-medium text-[var(--color-text-muted)]">
                    Key Themes
                  </p>
                  <InfoTooltip content={TOOLTIPS.keyThemes} position="right" />
                </div>
                <div className="flex flex-wrap gap-2">
                  {analysis.sentiment.key_themes.map((theme, i) => (
                    <span
                      key={i}
                      className={clsx(
                        'px-3 py-1.5 text-xs font-medium rounded-full',
                        'bg-gradient-to-r',
                        i % 3 === 0 && 'from-blue-500/10 to-purple-500/10 text-blue-400 border border-blue-500/20',
                        i % 3 === 1 && 'from-emerald-500/10 to-teal-500/10 text-emerald-400 border border-emerald-500/20',
                        i % 3 === 2 && 'from-orange-500/10 to-amber-500/10 text-orange-400 border border-orange-500/20'
                      )}
                    >
                      {theme}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* Fundamental Analysis */}
        <Card>
          <CardHeader
            title="Fundamental Metrics"
            subtitle="Financial health indicators"
            icon={<BarChart3 className="h-5 w-5" />}
          />

          <div className="space-y-4">
            <LinearGauge
              score={analysis.fundamental?.fundamental_score || 0}
              label="Overall Score"
              min={0}
              max={100}
            />

            <div className="grid grid-cols-2 gap-4">
              {analysis.fundamental?.pe_ratio && (
                <div>
                  <div className="flex items-center gap-1">
                    <p className="text-xs text-[var(--color-text-muted)]">P/E Ratio</p>
                    <InfoTooltip content={TOOLTIPS.peRatio} position="top" />
                  </div>
                  <p className="text-lg font-bold text-[var(--color-text-primary)]">
                    {analysis.fundamental.pe_ratio.toFixed(1)}
                  </p>
                </div>
              )}
              {analysis.fundamental?.peg_ratio && (
                <div>
                  <div className="flex items-center gap-1">
                    <p className="text-xs text-[var(--color-text-muted)]">PEG Ratio</p>
                    <InfoTooltip content={TOOLTIPS.pegRatio} position="top" />
                  </div>
                  <p className="text-lg font-bold text-[var(--color-text-primary)]">
                    {analysis.fundamental.peg_ratio.toFixed(2)}
                  </p>
                </div>
              )}
              {analysis.fundamental?.profit_margin && (
                <div>
                  <div className="flex items-center gap-1">
                    <p className="text-xs text-[var(--color-text-muted)]">Profit Margin</p>
                    <InfoTooltip content={TOOLTIPS.profitMargin} position="top" />
                  </div>
                  <p className="text-lg font-bold text-[var(--color-text-primary)]">
                    {formatPercent(analysis.fundamental.profit_margin * 100)}
                  </p>
                </div>
              )}
              {analysis.fundamental?.roe && (
                <div>
                  <div className="flex items-center gap-1">
                    <p className="text-xs text-[var(--color-text-muted)]">ROE</p>
                    <InfoTooltip content={TOOLTIPS.roe} position="top" />
                  </div>
                  <p className="text-lg font-bold text-[var(--color-text-primary)]">
                    {formatPercent(analysis.fundamental.roe * 100)}
                  </p>
                </div>
              )}
              {analysis.fundamental?.debt_to_equity && (
                <div>
                  <div className="flex items-center gap-1">
                    <p className="text-xs text-[var(--color-text-muted)]">Debt/Equity</p>
                    <InfoTooltip content={TOOLTIPS.debtToEquity} position="top" />
                  </div>
                  <p className="text-lg font-bold text-[var(--color-text-primary)]">
                    {analysis.fundamental.debt_to_equity.toFixed(2)}
                  </p>
                </div>
              )}
              {analysis.fundamental?.revenue_growth && (
                <div>
                  <div className="flex items-center gap-1">
                    <p className="text-xs text-[var(--color-text-muted)]">Revenue Growth</p>
                    <InfoTooltip content={TOOLTIPS.revenueGrowth} position="top" />
                  </div>
                  <p className="text-lg font-bold text-[var(--color-text-primary)]">
                    {formatPercent(analysis.fundamental.revenue_growth * 100)}
                  </p>
                </div>
              )}
            </div>

            {analysis.fundamental?.price_target && (
              <div className="p-4 rounded-lg bg-[var(--color-bg-secondary)]">
                <div className="flex justify-between items-center">
                  <div>
                    <div className="flex items-center gap-1">
                      <p className="text-sm text-[var(--color-text-muted)]">Analyst Target</p>
                      <InfoTooltip content={TOOLTIPS.priceTarget} position="right" />
                    </div>
                    <p className="text-xl font-bold text-[var(--color-text-primary)]">
                      {formatCurrency(analysis.fundamental.price_target)}
                    </p>
                  </div>
                  <Target className="h-8 w-8 text-[var(--color-accent)]" />
                </div>
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Sentiment Themes - AI-analyzed discussion themes */}
      {((analysis.sentiment?.popular_posts?.length ?? 0) > 0 || (analysis.sentiment?.recent_posts?.length ?? 0) > 0 || (analysis.sentiment?.notable_posts?.length ?? 0) > 0) && (
        <Card>
          <CardHeader
            title="Sentiment Themes"
            subtitle={analysis.sentiment?.daily_summary || "AI-analyzed discussion themes from social media"}
            icon={<MessageCircle className="h-5 w-5" />}
          />

          <div className="space-y-4">
            {/* Sentiment Character Badge */}
            {analysis.sentiment?.sentiment_character && (
              <div className="flex items-center gap-3 flex-wrap">
                <span className={clsx(
                  'px-3 py-1.5 rounded-full text-sm font-medium',
                  analysis.sentiment.sentiment_character === 'loved' && 'bg-emerald-500/20 text-emerald-400',
                  analysis.sentiment.sentiment_character === 'hated' && 'bg-red-500/20 text-red-400',
                  analysis.sentiment.sentiment_character === 'controversial' && 'bg-yellow-500/20 text-yellow-400',
                  analysis.sentiment.sentiment_character === 'hyped' && 'bg-purple-500/20 text-purple-400',
                  analysis.sentiment.sentiment_character === 'feared' && 'bg-orange-500/20 text-orange-400',
                  analysis.sentiment.sentiment_character === 'ignored' && 'bg-gray-500/20 text-gray-400',
                  !['loved', 'hated', 'controversial', 'hyped', 'feared', 'ignored'].includes(analysis.sentiment.sentiment_character) && 'bg-blue-500/20 text-blue-400'
                )}>
                  Currently {capitalizeWord(analysis.sentiment.sentiment_character)}
                </span>

                {/* Weekly Trend Badge */}
                {analysis.sentiment?.weekly_trend && (
                  <span className={clsx(
                    'px-2 py-1 rounded-full text-xs font-medium',
                    analysis.sentiment.weekly_trend === 'improving' && 'bg-emerald-500/10 text-emerald-400',
                    analysis.sentiment.weekly_trend === 'declining' && 'bg-red-500/10 text-red-400',
                    analysis.sentiment.weekly_trend === 'stable' && 'bg-gray-500/10 text-gray-400',
                    analysis.sentiment.weekly_trend === 'volatile' && 'bg-yellow-500/10 text-yellow-400'
                  )}>
                    {analysis.sentiment.weekly_trend === 'improving' && <TrendingUp className="h-3 w-3 inline mr-1" />}
                    {analysis.sentiment.weekly_trend === 'declining' && <TrendingDown className="h-3 w-3 inline mr-1" />}
                    {capitalizeWord(analysis.sentiment.weekly_trend)} this week
                  </span>
                )}
              </div>
            )}

            {/* Catchy Nicknames */}
            {analysis.sentiment?.catchy_nicknames && analysis.sentiment.catchy_nicknames.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {analysis.sentiment.catchy_nicknames.map((n, i) => (
                  <span
                    key={i}
                    className="px-2 py-1 bg-purple-500/10 text-purple-400 rounded text-xs cursor-help"
                    title={n.meaning}
                  >
                    "{n.nickname}"
                  </span>
                ))}
              </div>
            )}

            {/* Sentiment Posts - showing dates and authors */}
            <div className="space-y-3">
              {(() => {
                // Combine all posts for display
                const allPosts = [
                  ...(analysis.sentiment?.popular_posts || []),
                  ...(analysis.sentiment?.recent_posts || []),
                  ...(analysis.sentiment?.notable_posts || [])
                ];
                // Dedupe by text content
                const uniquePosts = allPosts.filter((post, index, self) =>
                  index === self.findIndex(p => p.text === post.text)
                );

                // Helper to format date - only show if it looks like a valid date
                const formatPostDate = (dateStr: string | undefined): string | null => {
                  if (!dateStr) return null;
                  // Check if it's in YYYY-MM-DD format
                  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
                    const date = new Date(dateStr + 'T00:00:00');
                    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                  }
                  return null; // Don't show relative times like "2 days ago"
                };

                return uniquePosts.slice(0, 5).map((post: NotablePost, i: number) => {
                  const sentiment = post.sentiment?.toLowerCase();
                  const isPositive = sentiment === 'bullish' || sentiment === 'pos' || sentiment === 'positive';
                  const isNegative = sentiment === 'bearish' || sentiment === 'neg' || sentiment === 'negative';
                  const formattedDate = formatPostDate(post.posted_at);

                  return (
                    <div
                      key={i}
                      className={clsx(
                        'p-3 rounded-lg border-l-4',
                        isPositive && 'bg-emerald-500/5 border-emerald-500',
                        isNegative && 'bg-red-500/5 border-red-500',
                        !isPositive && !isNegative && 'bg-gray-500/5 border-gray-500'
                      )}
                    >
                      <div className="flex items-start gap-3">
                        <div
                          className={clsx(
                            'flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center',
                            isPositive && 'bg-emerald-500/20',
                            isNegative && 'bg-red-500/20',
                            !isPositive && !isNegative && 'bg-gray-500/20'
                          )}
                        >
                          {isPositive && <ThumbsUp className="h-3.5 w-3.5 text-emerald-500" />}
                          {isNegative && <ThumbsDown className="h-3.5 w-3.5 text-red-500" />}
                          {!isPositive && !isNegative && <Minus className="h-3.5 w-3.5 text-gray-500" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-[var(--color-text-secondary)] leading-relaxed">
                            "{post.text}"
                          </p>
                          <div className="flex items-center gap-3 mt-1.5 text-xs text-[var(--color-text-muted)] flex-wrap">
                            {post.author && post.author !== 'anonymous' && (
                              <span>@{post.author}</span>
                            )}
                            {formattedDate && (
                              <span>{formattedDate}</span>
                            )}
                            <span
                              className={clsx(
                                'px-2 py-0.5 rounded-full',
                                isPositive && 'bg-emerald-500/10 text-emerald-500',
                                isNegative && 'bg-red-500/10 text-red-500',
                                !isPositive && !isNegative && 'bg-gray-500/10 text-gray-500'
                              )}
                            >
                              {isPositive ? 'Bullish' : isNegative ? 'Bearish' : 'Neutral'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                });
              })()}
            </div>

            {/* AI Disclaimer */}
            <p className="text-xs text-[var(--color-text-muted)] text-center pt-2 border-t border-[var(--color-border)]">
              Themes extracted by AI from social media discussions. Not direct quotes.
            </p>

            {/* Change Reasoning */}
            {analysis.sentiment?.change_reasoning && (
              <div className="p-4 rounded-lg bg-[var(--color-bg-secondary)] border-l-4 border-blue-500">
                <p className="text-sm text-[var(--color-text-secondary)]">
                  <span className="font-medium text-blue-400">Sentiment Shift: </span>
                  {analysis.sentiment.change_reasoning}
                </p>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* AI Insights */}
      {analysis.ai_insights && (
        <Card>
          <CardHeader
            title="AI Insights"
            subtitle="Powered by advanced language models"
            icon={<Sparkles className="h-5 w-5" />}
          />

          <div className="space-y-6">
            {analysis.ai_insights.summary && (
              <div className="p-4 rounded-lg bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/20">
                <p className="text-[var(--color-text-primary)]">
                  {analysis.ai_insights.summary}
                </p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {analysis.ai_insights.bull_case && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="h-5 w-5 text-emerald-500" />
                    <h4 className="font-semibold text-emerald-500">Bull Case</h4>
                  </div>
                  <p className="text-sm text-[var(--color-text-secondary)]">
                    {analysis.ai_insights.bull_case}
                  </p>
                </div>
              )}

              {analysis.ai_insights.bear_case && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingDown className="h-5 w-5 text-red-500" />
                    <h4 className="font-semibold text-red-500">Bear Case</h4>
                  </div>
                  <p className="text-sm text-[var(--color-text-secondary)]">
                    {analysis.ai_insights.bear_case}
                  </p>
                </div>
              )}
            </div>

            {analysis.ai_insights.catalysts && analysis.ai_insights.catalysts.length > 0 && (
              <div>
                <h4 className="font-semibold text-[var(--color-text-primary)] mb-2">
                  Upcoming Catalysts
                </h4>
                <ul className="space-y-2">
                  {analysis.ai_insights.catalysts.map((catalyst, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-2 text-sm text-[var(--color-text-secondary)]"
                    >
                      <Sparkles className="h-4 w-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                      {catalyst}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {analysis.ai_insights.risk_factors &&
              analysis.ai_insights.risk_factors.length > 0 && (
                <div>
                  <h4 className="font-semibold text-[var(--color-text-primary)] mb-2">
                    Risk Factors
                  </h4>
                  <ul className="space-y-2">
                    {analysis.ai_insights.risk_factors.map((risk, i) => (
                      <li
                        key={i}
                        className="flex items-start gap-2 text-sm text-[var(--color-text-secondary)]"
                      >
                        <AlertTriangle className="h-4 w-4 text-orange-500 mt-0.5 flex-shrink-0" />
                        {risk}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
          </div>
        </Card>
      )}

      {/* Disconnect Explanation */}
      <Card>
        <CardHeader
          title="Disconnect Analysis"
          subtitle="Why this signal was generated"
          icon={<Zap className="h-5 w-5" />}
        />

        <div className="space-y-4">
          <p className="text-[var(--color-text-secondary)]">
            {analysis.disconnect?.explanation || 'Analysis pending...'}
          </p>

          {analysis.disconnect?.key_factors && analysis.disconnect.key_factors.length > 0 && (
            <div>
              <h4 className="font-semibold text-[var(--color-text-primary)] mb-2">
                Key Factors
              </h4>
              <ul className="space-y-2">
                {analysis.disconnect.key_factors.map((factor, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-2 text-sm text-[var(--color-text-secondary)]"
                  >
                    <span className="h-2 w-2 rounded-full bg-[var(--color-accent)] mt-1.5 flex-shrink-0" />
                    {factor}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </Card>
    </>
  );
}
